import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import {
  TrendingUp, ShoppingBag, Package, AlertTriangle, ArrowUpRight, Receipt, Users,
} from "lucide-react";

const formatMXN = (n) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(n || 0);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [series, setSeries] = useState([]);

  useEffect(() => {
    api.get("/reports/dashboard").then((r) => setStats(r.data)).catch(() => {});
    api.get("/reports/sales-by-day", { params: { days: 7 } }).then((r) => setSeries(r.data));
  }, []);

  const max = Math.max(1, ...series.map((s) => s.total));

  const Kpi = ({ icon: Icon, label, value, hint, accent = "clay" }) => (
    <div className="bg-white p-6 rounded-xl border border-warm-border shadow-sm hover:-translate-y-0.5 transition-transform">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-stone/50 font-semibold">
            {label}
          </div>
          <div className="font-outfit text-4xl font-bold text-stone mt-2">{value}</div>
          {hint && <div className="text-xs text-stone/50 mt-1">{hint}</div>}
        </div>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-${accent}/10 text-${accent}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="admin-dashboard">
      <div className="mb-8 flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-clay font-semibold">Panel</div>
          <h1 className="font-outfit text-4xl font-bold text-stone">Dashboard</h1>
          <p className="text-stone/60 mt-1">Resumen rápido de tu losería.</p>
        </div>
        <Link
          to="/pos"
          data-testid="dashboard-go-pos"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-clay text-white font-medium hover:bg-clay-hover transition-all hover:-translate-y-0.5 shadow-[0_4px_14px_rgba(200,90,64,0.39)]"
        >
          Ir al punto de venta
          <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-stone p-6 rounded-xl text-bone shadow-sm hover:-translate-y-0.5 transition-transform">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-bone/60 font-semibold">
                Ventas hoy
              </div>
              <div className="font-outfit text-4xl font-bold mt-2" data-testid="kpi-today-total">
                {formatMXN(stats?.today?.total)}
              </div>
              <div className="text-xs text-bone/60 mt-1">
                {stats?.today?.count || 0} transacciones
              </div>
            </div>
            <div className="w-10 h-10 rounded-xl bg-clay/30 text-clay flex items-center justify-center">
              <TrendingUp className="w-5 h-5" />
            </div>
          </div>
        </div>
        <Kpi icon={ShoppingBag} label="Esta semana" value={formatMXN(stats?.week?.total)} hint={`${stats?.week?.count || 0} ventas`} />
        <Kpi icon={Package} label="Productos" value={stats?.total_products ?? "-"} hint="catálogo" />
        <Kpi icon={Users} label="Usuarios" value={stats?.total_users ?? "-"} hint="del sistema" accent="sage" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Sales chart */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-warm-border p-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-stone/50 font-semibold">
                Ventas
              </div>
              <h3 className="font-outfit text-xl font-bold text-stone">Últimos 7 días</h3>
            </div>
            <Link to="/admin/reports" className="text-sm text-clay hover:underline">
              Ver reportes
            </Link>
          </div>
          <div className="h-56 flex items-end gap-3" data-testid="sales-chart">
            {series.map((s) => {
              const h = (s.total / max) * 100;
              const dateStr = new Date(s.date + "T00:00:00").toLocaleDateString("es-MX", {
                weekday: "short",
                day: "numeric",
              });
              return (
                <div key={s.date} className="flex-1 flex flex-col items-center gap-2">
                  <div className="text-xs font-medium text-stone/70">{formatMXN(s.total)}</div>
                  <div
                    className="w-full bg-gradient-to-t from-clay to-clay/60 rounded-t-md transition-all hover:from-clay-hover"
                    style={{ height: `${Math.max(4, h)}%`, minHeight: "6px" }}
                  />
                  <div className="text-[10px] uppercase tracking-wider text-stone/50">
                    {dateStr}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Low stock */}
        <div className="bg-white rounded-xl border border-warm-border p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-4 h-4 text-amber" />
            <h3 className="font-outfit text-lg font-bold text-stone">Stock bajo</h3>
          </div>
          <div className="space-y-2" data-testid="low-stock-list">
            {(stats?.low_stock || []).length === 0 && (
              <div className="text-sm text-stone/50">Todo en orden.</div>
            )}
            {(stats?.low_stock || []).map((p) => (
              <div
                key={p.id}
                className="flex items-center justify-between p-3 bg-amber/5 border border-amber/20 rounded-lg"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-stone truncate">{p.name}</div>
                  <div className="text-xs text-stone/50 font-mono">{p.barcode}</div>
                </div>
                <div className="text-right">
                  <div className="font-outfit font-bold text-amber-600 text-sm" style={{ color: "#B97A30" }}>
                    {p.stock}
                  </div>
                  <div className="text-[10px] uppercase text-stone/40">stock</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent sales */}
      <div className="bg-white rounded-xl border border-warm-border mt-6 overflow-hidden shadow-sm">
        <div className="px-6 py-4 border-b border-warm-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Receipt className="w-4 h-4 text-clay" />
            <h3 className="font-outfit text-lg font-bold text-stone">Ventas recientes</h3>
          </div>
          <Link to="/admin/sales" className="text-sm text-clay hover:underline">
            Ver todas
          </Link>
        </div>
        <table className="w-full" data-testid="recent-sales-table">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-stone/50">
              <th className="px-6 py-3 font-semibold">Folio</th>
              <th className="px-6 py-3 font-semibold">Fecha</th>
              <th className="px-6 py-3 font-semibold">Cajero</th>
              <th className="px-6 py-3 font-semibold">Pago</th>
              <th className="px-6 py-3 font-semibold text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {(stats?.recent_sales || []).map((s) => (
              <tr key={s.id} className="border-t border-warm-border hover:bg-sand/60 transition-colors">
                <td className="px-6 py-3 font-mono text-sm font-medium text-stone">{s.folio}</td>
                <td className="px-6 py-3 text-sm text-stone/70">
                  {new Date(s.created_at).toLocaleString("es-MX")}
                </td>
                <td className="px-6 py-3 text-sm text-stone/70">{s.cashier_name}</td>
                <td className="px-6 py-3 text-xs uppercase tracking-wider text-stone/60">
                  {s.payment_method}
                </td>
                <td className="px-6 py-3 text-right font-outfit font-semibold text-stone">
                  {formatMXN(s.total)}
                </td>
              </tr>
            ))}
            {(!stats?.recent_sales || stats.recent_sales.length === 0) && (
              <tr>
                <td colSpan={5} className="px-6 py-10 text-center text-stone/50">
                  Aún no hay ventas registradas.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
